// @flow strict
import parseUrl from '../url/parseUrl.js';
import {
  FIRST_PARTY_ANALYTICS_ENABLED,
  FIRST_PARTY_ANALYTICS_ENDPOINT,
  FIRST_PARTY_ANALYTICS_RELEASE,
} from '../../../../etc/appConfig.js';
import { COOKIES } from '../../records/Cookies.js';
import { classifyEntryReferral } from './entryReferral.js';
import type { EntryReferral } from './entryReferral.js';

export const ANALYTICS_SCHEMA_VERSION = 1;
export const MAX_BATCH_EVENTS = 10;
export const MAX_BATCH_BYTES = 16 * 1024;
export const MAX_EVENT_BYTES = 1024;

const FLUSH_EVENT_COUNT = 5;
const FLUSH_INTERVAL_MS = 5000;
export const ANALYTICS_OPTOUT_COOKIE = 'tl_analytics_optout';

const ROUTE_KINDS = new Set([
  'landing',
  'ai',
  'destinations',
  'destination',
  'category',
  'subcategory',
  'pois',
  'poiToursSection',
  'poi',
  'poiCategory',
  'groupTour',
  'forwardtopartner',
  'author',
  'magazineIndex',
  'magazineArticle',
  'magazineRedirect',
  'imprint',
  'privacypolicy',
  'termsandconditions',
  'search',
  'notfound',
]);

const EVENT_NAMES = new Set([
  'page_view',
  'product_clicked',
  'partner_clicked',
  'list_rendered',
  'chat_opened',
  'web_vital',
  'client_operation_failed',
  'review_experiment_exposed',
  'review_load_result',
  'review_section_viewed',
]);
const LIST_SURFACES = new Set(['search', 'tour', 'offer']);
const CHAT_ENTRIES = new Set(['launcher', 'speaking_bubble']);
const VITAL_METRICS = new Set(['LCP', 'INP', 'CLS']);
const VITAL_RATINGS = new Set(['good', 'needs_improvement', 'poor']);
const FAILURE_OPERATIONS = new Set(['search', 'list', 'chat_stream']);
const FAILURE_CLASSES = new Set([
  'network',
  'timeout',
  'http_4xx',
  'http_5xx',
  'invalid_response',
  'unknown',
]);
const REVIEW_EXPERIMENT_VARIANTS = new Set(['control', 'reviews']);
const REVIEW_LOAD_OUTCOMES = new Set(['success', 'partial', 'empty', 'error', 'timeout']);
const MAX_REVIEW_DURATION_MS = 120000;
const MAX_REVIEW_COUNT = 100;
const MAX_REVIEW_PROVIDER_COUNT = 20;

type AnalyticsRoute = {
  kind: string,
  locale: string,
  destination_id?: number,
  group_id?: number,
  poi_id?: number,
  poi_tours_section_id?: number,
  category_id?: number,
  tour_id?: number,
};

type AnalyticsEvent = {
  schema_version: 1,
  release: string,
  event_id: string,
  name: string,
  browser_automation: BrowserAutomation,
  entry_referral?: EntryReferral,
  route: AnalyticsRoute,
  properties: { [string]: boolean | number | string },
};

type BrowserAutomation = 'none' | 'webdriver' | 'phantom' | 'nightmare' | 'cypress' | 'unavailable';

type ClientOptions = {
  enabled?: boolean,
  endpoint?: string,
  release?: string,
  globalObject?: any,
  fetchImpl?: (...args: any[]) => any,
  createEventId?: () => string,
  setTimer?: (callback: () => void, delay: number) => any,
  clearTimer?: (timer: any) => void,
};

const isPositiveInteger = (value: mixed): boolean =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const isSignedNonZeroInteger = (value: mixed): boolean =>
  typeof value === 'number' &&
  Number.isSafeInteger(value) &&
  value !== 0 &&
  value >= -2147483648 &&
  value <= 2147483647;

export const byteLength = (value: string): number => {
  let length = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x7f) {
      length += 1;
    } else if (code <= 0x7ff) {
      length += 2;
    } else if (code >= 0xd800 && code <= 0xdbff && index + 1 < value.length) {
      length += 4;
      index += 1;
    } else {
      length += 3;
    }
  }
  return length;
};

const sanitizeRelease = (release: mixed): string => {
  if (typeof release !== 'string') {
    return 'unknown';
  }
  const normalized = release.trim().slice(0, 64);
  return normalized && /^[A-Za-z0-9][A-Za-z0-9._+-]{0,63}$/.test(normalized)
    ? normalized
    : 'unknown';
};

const randomHex = (length: number): string => {
  let result = '';
  for (let index = 0; index < length; index += 1) {
    result += Math.floor(Math.random() * 16).toString(16);
  }
  return result;
};

export const createEventId = (
  globalObject: any = typeof window === 'undefined' ? null : window,
): string => {
  const cryptoObject = globalObject && globalObject.crypto;
  if (cryptoObject && typeof cryptoObject.randomUUID === 'function') {
    return cryptoObject.randomUUID();
  }
  if (cryptoObject && typeof cryptoObject.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoObject.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
      16,
      20,
    )}-${hex.slice(20)}`;
  }
  return `${randomHex(8)}-${randomHex(4)}-4${randomHex(3)}-a${randomHex(3)}-${randomHex(12)}`;
};

export const normalizeRoute = (pathname: string): AnalyticsRoute => {
  const parsed = parseUrl(typeof pathname === 'string' ? pathname : '');
  const route: AnalyticsRoute = {
    kind: ROUTE_KINDS.has(parsed.pageType) ? parsed.pageType : 'notfound',
    locale: parsed.language,
  };

  if (isPositiveInteger(parsed.destination)) route.destination_id = Number(parsed.destination);
  if (isSignedNonZeroInteger(parsed.group)) route.group_id = Number(parsed.group);
  if (isPositiveInteger(parsed.poi)) route.poi_id = Number(parsed.poi);
  if (isPositiveInteger(parsed.poiToursSection)) {
    route.poi_tours_section_id = Number(parsed.poiToursSection);
  }
  if (isPositiveInteger(Number(parsed.category))) route.category_id = Number(parsed.category);
  if (isPositiveInteger(parsed.tour)) route.tour_id = Number(parsed.tour);

  return route;
};

export const resultCountBucket = (count: number): string => {
  if (!Number.isFinite(count) || count <= 0) return '0';
  if (count <= 4) return '1-4';
  if (count <= 9) return '5-9';
  if (count <= 24) return '10-24';
  return '25+';
};

export const queryLengthBucket = (length: number): string => {
  if (!Number.isFinite(length) || length <= 0) return '0';
  if (length <= 3) return '1-3';
  if (length <= 10) return '4-10';
  return '11+';
};

export const classifyClientFailure = (error: any): string => {
  if (!error) return 'unknown';
  if (error.name === 'AbortError') return 'unknown';
  if (error.name === 'TimeoutError') return 'timeout';
  if (error.name === 'InvalidResponseError') return 'invalid_response';
  const status = Number(error.status);
  if (status >= 400 && status < 500) return 'http_4xx';
  if (status >= 500 && status < 600) return 'http_5xx';
  if (error instanceof TypeError || error.name === 'TypeError') return 'network';
  return 'unknown';
};

export const hasAnalyticsOptOut = (cookieValue: string): boolean =>
  cookieValue.split(';').some((cookie) => {
    const [name, value] = cookie.trim().split('=');
    return name === ANALYTICS_OPTOUT_COOKIE && value === '1';
  });

export const hasBrowserPrivacySignal = (globalObject: any): boolean => {
  if (!globalObject) return true;
  const navigatorObject = globalObject.navigator || {};
  return Boolean(
    navigatorObject.globalPrivacyControl === true ||
      navigatorObject.doNotTrack === '1' ||
      navigatorObject.msDoNotTrack === '1' ||
      globalObject.doNotTrack === '1',
  );
};

export const isPrivacySuppressed = (globalObject: any): boolean => {
  if (!globalObject) return true;
  try {
    if (hasBrowserPrivacySignal(globalObject)) return true;
    return Boolean(globalObject.document && hasAnalyticsOptOut(globalObject.document.cookie || ''));
  } catch (error) {
    return true;
  }
};

export const detectBrowserAutomation = (globalObject: any): BrowserAutomation => {
  try {
    if (!globalObject || typeof globalObject !== 'object') return 'unavailable';
    const navigatorObject = globalObject.navigator;
    if (!navigatorObject || typeof navigatorObject !== 'object') return 'unavailable';

    // The standard Navigator flag can be a prototype getter; only an exact true is evidence.
    if (navigatorObject.webdriver === true) return 'webdriver';

    const hasMarker = (name: string, booleanMarker = false): boolean => {
      // Ignore inherited marker names, including Object.prototype pollution.
      if (!Object.prototype.hasOwnProperty.call(globalObject, name)) return false;
      const marker = globalObject[name];
      return (
        (booleanMarker && marker === true) ||
        (marker !== null && (typeof marker === 'object' || typeof marker === 'function'))
      );
    };

    if (hasMarker('_phantom')) return 'phantom';
    if (hasMarker('__nightmare', true)) return 'nightmare';
    if (hasMarker('Cypress')) return 'cypress';
    return 'none';
  } catch (error) {
    // A blocked getter is an unknown signal, never evidence of an ordinary browser.
    return 'unavailable';
  }
};

const isBoundedInteger = (value: mixed, max: number): boolean =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= max;

const validExperimentIdentity = (properties: any): boolean =>
  properties.experiment_id === 'group_reviews' &&
  REVIEW_EXPERIMENT_VARIANTS.has(properties.experiment_variant);

const validExperimentBase = (properties: any): boolean =>
  validExperimentIdentity(properties) && properties.experiment_version === 1;

const validEventProperties = (name: string, route: AnalyticsRoute, properties: any): boolean => {
  const keys = Object.keys(properties || {}).sort();
  if (name === 'product_clicked' || name === 'partner_clicked') return keys.length === 0;
  if (name === 'page_view') return keys.length === 0;
  if (name === 'list_rendered') {
    return (
      keys.join(',') === 'date_selected,query_length_bucket,result_count_bucket,surface' &&
      LIST_SURFACES.has(properties.surface) &&
      ['0', '1-4', '5-9', '10-24', '25+'].includes(properties.result_count_bucket) &&
      ['0', '1-3', '4-10', '11+'].includes(properties.query_length_bucket) &&
      typeof properties.date_selected === 'boolean'
    );
  }
  if (name === 'chat_opened') {
    return keys.join(',') === 'entry' && CHAT_ENTRIES.has(properties.entry);
  }
  if (name === 'web_vital') {
    return (
      (keys.join(',') === 'metric,rating' || (keys.join(',') === 'method,metric,rating,value' &&
        properties.method === 'web-vitals-5' && Number.isFinite(properties.value) && properties.value >= 0 &&
        properties.value <= (properties.metric === 'CLS' ? 100 : 3600000) &&
        vitalRating(properties.metric, properties.value) === properties.rating)) &&
      VITAL_METRICS.has(properties.metric) &&
      VITAL_RATINGS.has(properties.rating)
    );
  }
  if (name === 'client_operation_failed') {
    return (
      keys.join(',') === 'failure_class,operation' &&
      FAILURE_OPERATIONS.has(properties.operation) &&
      FAILURE_CLASSES.has(properties.failure_class)
    );
  }
  if (name === 'review_experiment_exposed') {
    return (
      route.kind === 'groupTour' &&
      isSignedNonZeroInteger(route.group_id) &&
      keys.join(',') === 'eligible,experiment_id,experiment_variant,experiment_version' &&
      validExperimentIdentity(properties) &&
      (properties.experiment_version === 0 || properties.experiment_version === 1) &&
      properties.eligible === true
    );
  }
  if (name === 'review_load_result') {
    return (
      route.kind === 'groupTour' &&
      isSignedNonZeroInteger(route.group_id) &&
      keys.join(',') ===
        'duration_ms,experiment_id,experiment_variant,experiment_version,outcome,provider_count,review_count' &&
      validExperimentBase(properties) &&
      properties.experiment_variant === 'reviews' &&
      REVIEW_LOAD_OUTCOMES.has(properties.outcome) &&
      isBoundedInteger(properties.duration_ms, MAX_REVIEW_DURATION_MS) &&
      isBoundedInteger(properties.review_count, MAX_REVIEW_COUNT) &&
      isBoundedInteger(properties.provider_count, MAX_REVIEW_PROVIDER_COUNT)
    );
  }
  if (name === 'review_section_viewed') {
    return (
      route.kind === 'groupTour' &&
      isSignedNonZeroInteger(route.group_id) &&
      keys.join(',') ===
        'experiment_id,experiment_variant,experiment_version,provider_count,review_count' &&
      validExperimentBase(properties) &&
      properties.experiment_variant === 'reviews' &&
      isBoundedInteger(properties.review_count, MAX_REVIEW_COUNT) &&
      isBoundedInteger(properties.provider_count, MAX_REVIEW_PROVIDER_COUNT)
    );
  }
  return false;
};

export const createAnalyticsClient = (options: ClientOptions = {}) => {
  const globalObject = options.globalObject || (typeof window === 'undefined' ? null : window);
  const enabled = options.enabled === true;
  const endpoint = options.endpoint || FIRST_PARTY_ANALYTICS_ENDPOINT;
  const release = sanitizeRelease(options.release || FIRST_PARTY_ANALYTICS_RELEASE);
  const fetchImpl =
    options.fetchImpl ||
    (globalObject && globalObject.fetch ? globalObject.fetch.bind(globalObject) : null);
  const setTimer =
    options.setTimer ||
    (globalObject && globalObject.setTimeout
      ? globalObject.setTimeout.bind(globalObject)
      : setTimeout);
  const clearTimer =
    options.clearTimer ||
    (globalObject && globalObject.clearTimeout
      ? globalObject.clearTimeout.bind(globalObject)
      : clearTimeout);
  const eventIdFactory = options.createEventId || (() => createEventId(globalObject));
  const queue: AnalyticsEvent[] = [];
  const reportedVitals = new Set();
  let failureCount = 0;
  let timer = null;
  let inFlight = false;
  let lifecycleAttached = false;
  let pageViewAttempted = false;

  const isActive = (): boolean =>
    Boolean(enabled && globalObject && fetchImpl && !isPrivacySuppressed(globalObject));

  const clearFlushTimer = () => {
    if (timer) {
      clearTimer(timer);
      timer = null;
    }
  };

  const buildBody = (events: AnalyticsEvent[]): string =>
    JSON.stringify({ schema_version: ANALYTICS_SCHEMA_VERSION, events });

  const send = async (body: string): Promise<void> => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        // Public ingestion deliberately uses no auth headers or browser credentials.
        const response = await fetchImpl(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          body,
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          cache: 'no-store',
          keepalive: true,
        });
        if (response && response.ok) return;
      } catch (error) {
        // One bounded retry; analytics must never affect the product experience.
      }
    }
  };

  const flush = async (): Promise<void> => {
    if (inFlight) return;
    clearFlushTimer();
    if (!isActive()) {
      queue.splice(0, queue.length);
      return;
    }
    if (!queue.length) return;

    const events = queue.splice(0, MAX_BATCH_EVENTS);
    const body = buildBody(events);
    if (byteLength(body) > MAX_BATCH_BYTES) return;

    inFlight = true;
    await send(body);
    inFlight = false;

    if (queue.length >= FLUSH_EVENT_COUNT) {
      flush();
    } else if (queue.length) {
      timer = setTimer(flush, FLUSH_INTERVAL_MS);
    }
  };

  const attachLifecycle = () => {
    if (lifecycleAttached || !globalObject || !globalObject.addEventListener) return;
    lifecycleAttached = true;
    globalObject.addEventListener('pagehide', flush);
  };

  const enqueue = (
    name: string,
    route: AnalyticsRoute,
    properties: any,
    immediate = false,
    initialDocument = false,
  ) => {
    if (!isActive() || !EVENT_NAMES.has(name) || !validEventProperties(name, route, properties)) {
      return;
    }
    const event: AnalyticsEvent = {
      schema_version: ANALYTICS_SCHEMA_VERSION,
      release,
      event_id: eventIdFactory(),
      name,
      browser_automation: detectBrowserAutomation(globalObject),
      route,
      properties,
    };
    if (name === 'page_view') {
      event.entry_referral = initialDocument ? classifyEntryReferral(globalObject) : 'not_entry';
    }
    if (byteLength(JSON.stringify(event)) > MAX_EVENT_BYTES || queue.length >= MAX_BATCH_EVENTS) {
      return;
    }

    attachLifecycle();
    queue.push(event);
    if (immediate || queue.length >= FLUSH_EVENT_COUNT) {
      flush();
    } else if (!timer) {
      timer = setTimer(flush, FLUSH_INTERVAL_MS);
    }
  };

  const currentRoute = (): AnalyticsRoute =>
    normalizeRoute(globalObject && globalObject.location ? globalObject.location.pathname : '');

  const trackPageView = (pathname: string, immediate = false, initialDocument = false) => {
    const captureEntry = initialDocument === true && !pageViewAttempted;
    // Consume the document opportunity even if the first attempt is suppressed.
    // Later opt-in or a component remount must not recreate an earlier arrival.
    pageViewAttempted = true;
    enqueue('page_view', normalizeRoute(pathname), {}, immediate, captureEntry);
  };

  const trackFunnelLink = (href: string) => {
    // Inspect only a bounded same-site route, after the existing privacy gate.
    // Query parameters and destination identifiers never enter the click event.
    if (!isActive() || typeof href !== 'string' || href.length > 4096 ||
        !href.startsWith('/') || href.startsWith('//')) return;
    const destination = normalizeRoute(href.split(/[?#]/)[0]);
    if (destination.kind !== 'groupTour' && destination.kind !== 'forwardtopartner') return;
    const route = currentRoute();
    enqueue(destination.kind === 'groupTour' ? 'product_clicked' : 'partner_clicked', route, {}, true);
  };

  const trackListRendered = ({ surface, resultCount, queryLength, dateSelected }) => {
    if (!LIST_SURFACES.has(surface)) return;
    const route = currentRoute();
    enqueue('list_rendered', route, {
      surface,
      result_count_bucket: resultCountBucket(Number(resultCount)),
      query_length_bucket: queryLengthBucket(Number(queryLength)),
      date_selected: Boolean(dateSelected),
    });
  };

  const trackChatOpened = (entry: string) => {
    if (CHAT_ENTRIES.has(entry)) enqueue('chat_opened', currentRoute(), { entry });
  };

  const trackWebVital = (metric: string, rating: string, pathname?: string, value?: number) => {
    if (!VITAL_METRICS.has(metric) || !VITAL_RATINGS.has(rating) || reportedVitals.has(metric)) {
      return;
    }
    reportedVitals.add(metric);
    enqueue('web_vital', pathname ? normalizeRoute(pathname) : currentRoute(), { metric, rating,
      ...(value === undefined ? {} : { value, method: 'web-vitals-5' }),
    });
  };

  const trackClientOperationFailed = (operation: string, failureClass: string) => {
    if (
      failureCount >= 3 ||
      !FAILURE_OPERATIONS.has(operation) ||
      !FAILURE_CLASSES.has(failureClass)
    ) {
      return;
    }
    failureCount += 1;
    enqueue('client_operation_failed', currentRoute(), {
      operation,
      failure_class: failureClass,
    });
  };

  const trackReviewExperimentExposed = (variant: string, version: number) => {
    enqueue(
      'review_experiment_exposed',
      currentRoute(),
      {
        experiment_id: 'group_reviews',
        experiment_version: version,
        experiment_variant: variant,
        eligible: true,
      },
      true,
    );
  };

  const trackReviewLoadResult = ({
    variant,
    outcome,
    durationMs,
    reviewCount,
    providerCount,
  }: any) => {
    enqueue(
      'review_load_result',
      currentRoute(),
      {
        experiment_id: 'group_reviews',
        experiment_version: 1,
        experiment_variant: variant,
        outcome,
        duration_ms: Math.round(Number(durationMs)),
        review_count: Number(reviewCount),
        provider_count: Number(providerCount),
      },
      true,
    );
  };

  const trackReviewSectionViewed = ({ variant, reviewCount, providerCount }: any) => {
    enqueue(
      'review_section_viewed',
      currentRoute(),
      {
        experiment_id: 'group_reviews',
        experiment_version: 1,
        experiment_variant: variant,
        review_count: Number(reviewCount),
        provider_count: Number(providerCount),
      },
      true,
    );
  };

  const destroy = () => {
    clearFlushTimer();
    if (lifecycleAttached && globalObject && globalObject.removeEventListener) {
      globalObject.removeEventListener('pagehide', flush);
    }
    lifecycleAttached = false;
    queue.splice(0, queue.length);
    reportedVitals.clear();
    failureCount = 0;
    // pageViewAttempted belongs to this document, not the resettable queue.
  };

  return {
    destroy,
    flush,
    isActive,
    trackPageView,
    trackFunnelLink,
    trackListRendered,
    trackChatOpened,
    trackWebVital,
    trackClientOperationFailed,
    trackReviewExperimentExposed,
    trackReviewLoadResult,
    trackReviewSectionViewed,
    // Exposed only for deterministic unit tests; the production queue remains memory-only.
    getQueueLength: () => queue.length,
  };
};

export const analyticsClient = createAnalyticsClient({
  enabled: FIRST_PARTY_ANALYTICS_ENABLED,
  endpoint: FIRST_PARTY_ANALYTICS_ENDPOINT,
  release: FIRST_PARTY_ANALYTICS_RELEASE,
});

export const setAnalyticsOptOut = (
  optedOut: boolean,
  globalObject: any = typeof window === 'undefined' ? null : window,
): boolean => {
  if (!globalObject || !globalObject.document) return false;
  try {
    const secure =
      globalObject.location && globalObject.location.protocol === 'https:' ? '; Secure' : '';
    globalObject.document.cookie = optedOut
      ? `${ANALYTICS_OPTOUT_COOKIE}=1; Path=/; Max-Age=31536000; SameSite=Lax${secure}`
      : `${ANALYTICS_OPTOUT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
    if (optedOut) {
      globalObject.document.cookie = `${COOKIES.GROUP_REVIEWS_EXPERIMENT}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
      analyticsClient.destroy();
    }
    return true;
  } catch (error) {
    return false;
  }
};

export const trackPageView = (pathname: string, immediate?: boolean, initialDocument?: boolean) =>
  analyticsClient.trackPageView(pathname, immediate, initialDocument);
export const trackFunnelLink = (href: string) => analyticsClient.trackFunnelLink(href);
export const trackListRendered = (properties: any) => analyticsClient.trackListRendered(properties);
export const trackChatOpened = (entry: string) => analyticsClient.trackChatOpened(entry);
export const trackClientOperationFailed = (operation: string, failureClass: string) =>
  analyticsClient.trackClientOperationFailed(operation, failureClass);
export const trackReviewExperimentExposed = (variant: string, version: number) =>
  analyticsClient.trackReviewExperimentExposed(variant, version);
export const trackReviewLoadResult = (properties: any) =>
  analyticsClient.trackReviewLoadResult(properties);
export const trackReviewSectionViewed = (properties: any) =>
  analyticsClient.trackReviewSectionViewed(properties);

const vitalRating = (metric: string, value: number): string => {
  if (metric === 'LCP')
    return value <= 2500 ? 'good' : value <= 4000 ? 'needs_improvement' : 'poor';
  if (metric === 'INP') return value <= 200 ? 'good' : value <= 500 ? 'needs_improvement' : 'poor';
  return value <= 0.1 ? 'good' : value <= 0.25 ? 'needs_improvement' : 'poor';
};

// Keep raw timing entries, DOM attribution and metric IDs inside the browser.
// Only the first finalized measurement per metric/document is sent (through its
// first foreground interval). BFCache restores do not create another sample.
export const observeWebVitals = ({
  client = analyticsClient,
  globalObject = typeof window === 'undefined' ? null : window,
  random = Math.random,
  loadVitals = () => import('web-vitals'),
}: any = {}): (() => void) => {
  if (!client.isActive() || !globalObject || random() >= 0.01) return () => {};
  let stopped = false;
  const initialPathname = globalObject.location ? globalObject.location.pathname : '';
  Promise.resolve(loadVitals()).then(({ onLCP, onINP, onCLS }) => {
    if (stopped || !client.isActive()) return;
    const report = ({ name, value }) => {
      if (stopped || !Number.isFinite(value) || value < 0) return;
      const rounded = name === 'CLS' ? Math.round(value * 10000) / 10000 : Math.round(value);
      // Drop implausible values rather than clipping them into a misleading percentile.
      if (rounded > (name === 'CLS' ? 100 : 3600000)) return;
      client.trackWebVital(name, vitalRating(name, rounded), initialPathname, rounded);
      client.flush();
    };
    onLCP(report);
    onINP(report);
    onCLS(report);
  }).catch(() => {}); // Performance measurement must never interrupt the website.
  return () => { stopped = true; };
};

let webVitalsStarted = false;
export const startWebVitals = () => {
  if (!webVitalsStarted) {
    webVitalsStarted = true;
    observeWebVitals();
  }
};
